import React, { useState, useEffect, useMemo } from 'react';
import { Card, Badge, Button, Form, Collapse, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getGoogleMapsLink, safeFormatDate, getOptionBadgeColor, renderAddressWithEnglish, formatDateWithLocale, getGoogleMapsDirectionsUrl } from '../../shared/utils';
import { isRTL } from '../../i18n/config';
import { getLinkIconDisplay } from '../../shared/linkIcons';
import ActivityDescription from './ActivityDescription';

export default function AccommodationActivity({ activity, dayId, onEdit, onDelete, onUpdateCompletion, onUpdateDescriptionAlign, collapseCompletedActivities, forceCollapsed, nextActivityInfo }) {
  const { t, i18n } = useTranslation();
  // Local state for content visibility
  const [showContent, setShowContent] = useState(!activity.completed || !collapseCompletedActivities);
  // Local state for completion status
  const [isCompleted, setIsCompleted] = useState(activity.completed || false);
  
  // Update content visibility when completion status changes
  useEffect(() => {
    // If collapseCompletedActivities is true and activity is completed, collapse the content
    if (collapseCompletedActivities && activity.completed) {
      setShowContent(false);
    }
  }, [activity.completed, collapseCompletedActivities]);
  
  // Update content visibility when forceCollapsed prop changes
  useEffect(() => {
    if (forceCollapsed !== undefined) {
      setShowContent(!forceCollapsed);
    }
  }, [forceCollapsed]);

  // Calculate "Next Stop" link from pre-calculated next activity
  const nextStopLink = useMemo(() => {
    if (!nextActivityInfo || !nextActivityInfo.activity) return null;
    const directionsUrl = getGoogleMapsDirectionsUrl(activity, nextActivityInfo.activity, nextActivityInfo.role);
    if (!directionsUrl) return null;
    return { name: t('planDetail.days.nextStop'), url: directionsUrl, isPrivate: false };
  }, [activity, nextActivityInfo, t]);
  
  // Handle completion toggle without opening edit popup
  const handleCompletionToggle = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    const newCompletedState = e.target.checked;
    
    // Update local state immediately for UI feedback
    setIsCompleted(newCompletedState);
    
    // If collapseCompletedActivities is true and activity is being marked as completed, collapse the content
    if (collapseCompletedActivities && newCompletedState) {
      setShowContent(false);
    } else if (!newCompletedState) {
      // If activity is being marked as not completed, expand the content
      setShowContent(true);
    }
    
    // Update the activity with the new completed status
    const updatedActivity = {
      ...activity,
      completed: newCompletedState
    };
    
    // Save changes directly without opening edit modal
    if (onUpdateCompletion) {
      onUpdateCompletion(updatedActivity, dayId);
    }
  };
  
  // Toggle content visibility
  const toggleContent = (e) => {
    e.stopPropagation();
    setShowContent(!showContent);
  };

  return (
    <Card className={`mb-2 shadow-sm border-success ${isCompleted ? 'bg-light' : ''}`}>
      <Card.Body className="px-2 px-md-3">
        {/* Header with responsive layout - single row on md+ screens, two rows on small screens */}
        <div className="mb-2 d-flex flex-column flex-md-row justify-content-md-between align-items-md-center">
          <div className="d-flex align-items-center flex-wrap">
            <h5 className={`mb-2 mb-md-0 ${isCompleted ? 'text-muted' : ''}`}>
              <i className="bi bi-house-door me-2 text-success"></i>
              {activity.name}
              {activity.nameInEnglish && activity.name !== activity.nameInEnglish && (
                <span className="text-muted"> ({activity.nameInEnglish})</span>
              )}
            </h5>
          </div>
          <div className="d-flex align-items-center justify-content-between mt-2 mt-md-0">
            {/* Left side (LTR) / Right side (RTL): Completed switch + badge */}
            <div className="d-flex align-items-center gap-2">
              <div className="completed-switch-wrapper" onClick={(e) => e.stopPropagation()}>
                <Form.Check 
                  type="switch"
                  id={`completed-switch-${activity.id}`}
                  checked={isCompleted}
                  onChange={handleCompletionToggle}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {isCompleted && (
                <Badge bg="success" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{t('planDetail.days.completed')}</Badge>
              )}
            </div>
            
            {/* Right side (LTR) / Left side (RTL): Action buttons */}
            <div className="d-flex align-items-center gap-2">
              {/* Dropdown menu for small screens */}
              <Dropdown className="d-md-none activity-menu-dropdown" drop="down" onClick={(e) => e.stopPropagation()}>
                <Dropdown.Toggle 
                  variant="outline-secondary" 
                  size="sm"
                  id={`activity-menu-${activity.id}`}
                >
                  <i className="bi bi-three-dots-vertical"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu align={isRTL(i18n.language) ? 'start' : 'end'}>
                  <Dropdown.Item onClick={() => onEdit(activity, dayId)}>
                    <i className="bi bi-pencil me-2"></i>
                    {t('common.edit')}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onDelete(activity, dayId)} className="text-danger">
                    <i className="bi bi-trash me-2"></i>
                    {t('common.delete')}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {/* Edit and Delete buttons - visible on medium screens and up */}
              <Button 
                variant="outline-secondary" 
                size="sm"
                className="d-none d-md-inline-block"
                onClick={() => onEdit(activity, dayId)}
              >
                <i className="bi bi-pencil"></i>
              </Button>
              <Button 
                variant="outline-danger" 
                size="sm"
                className="d-none d-md-inline-block"
                onClick={() => onDelete(activity, dayId)}
              >
                <i className="bi bi-trash"></i>
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={toggleContent}
              >
                <i className={`bi ${showContent ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Activity content taking full width */}
        <Collapse in={showContent}>
          <div>
            {activity.startDateTime && (
              <div className="text-muted mb-2 d-flex align-items-center flex-wrap">
                <i className="bi bi-calendar me-1"></i>
                {formatDateWithLocale(activity.startDateTime, 'PP')} {safeFormatDate(activity.startDateTime,'time')}
                {activity.endDateTime && ` → ${formatDateWithLocale(activity.endDateTime, 'PP')}`}
                <Badge bg="success" className="ms-2">{t('planDetail.days.accommodation')}</Badge>
                {/* Show option badges - only show if specific options are selected (not "all options") */}
                {(() => {
                  const options = activity.option;
                  // If option is empty array, undefined/null, or "all", don't show badge (means all options)
                  if (!options || (Array.isArray(options) && options.length === 0) || options === 'all') {
                    return null;
                  }
                  // If option is array with values, show tags with different colors
                  if (Array.isArray(options) && options.length > 0) {
                    return (
                      <div className="d-flex gap-1 ms-2">
                        {options.map(opt => (
                          <Badge key={opt} bg={getOptionBadgeColor(opt)} style={{ fontSize: '0.7rem' }}>{t('planDetail.days.option')} {opt}</Badge>
                        ))}
                      </div>
                    );
                  }
                  // If option is a single number, show tag
                  if (typeof options === 'number') {
                    return <Badge bg={getOptionBadgeColor(options)} className="ms-2" style={{ fontSize: '0.7rem' }}>{t('planDetail.days.option')} {options}</Badge>;
                  }
                  return null;
                })()}
              </div>
            )}

            {/* Check-in and check-out times on the same row */}
            <div className={`mb-3 ${isRTL(i18n.language) ? 'pe-2 border-end' : 'ps-2 border-start'} border-success`}>
              <div className="d-flex" style={{ gap: isRTL(i18n.language) ? '1.5rem' : '1.5rem' }}>
                <div>
                  <i className={`bi ${isRTL(i18n.language) ? 'bi-box-arrow-in-left' : 'bi-box-arrow-in-right'} ${isRTL(i18n.language) ? 'ms-1' : 'me-1'}`}></i>
                  <strong>{t('planDetail.days.checkIn')}:</strong> {activity.checkInTime || t('planDetail.days.notSpecified')}
                </div>
                <div>
                  <i className={`bi ${isRTL(i18n.language) ? 'bi-box-arrow-left' : 'bi-box-arrow-right'} ${isRTL(i18n.language) ? 'ms-1' : 'me-1'}`}></i>
                  <strong>{t('planDetail.days.checkOut')}:</strong> {activity.checkOutTime || t('planDetail.days.notSpecified')}
                </div>
              </div>
            </div>

            <ActivityDescription
              activity={activity}
              dayId={dayId}
              isCompleted={isCompleted}
              onUpdateDescriptionAlign={onUpdateDescriptionAlign}
            />

            {/* Location with Google Maps link */}
            {activity.location && (
              <p className="text-muted small mb-1">
                <i className="bi bi-geo-alt me-1"></i>
                {renderAddressWithEnglish(activity.location)}
              </p>
            )}

            {activity.notes && (
              <p className="text-muted small mb-1"><strong>{t('planDetail.days.notes')}:</strong> {activity.notes}</p>
            )}

            {activity.cost?.value != null && activity.cost?.value !== '' && (
              <p className="text-muted small mb-1">
                <strong>{t('planDetail.days.cost')}:</strong> {Number(activity.cost.value).toLocaleString()} {activity.cost.currency || ''}
                {activity.cost.rates && activity.cost.rates.date && (
                  <Badge bg="success" className="ms-2" style={{ fontSize: '0.7rem' }}>{t('planDetail.days.paid')}</Badge>
                )}
              </p>
            )}

            {((activity.links && activity.links.length > 0) || nextStopLink) && (
              <div className="d-flex flex-wrap gap-1 mt-2 align-items-center">
                {activity.links && activity.links.map((link, i) => {
                  const iconDisplay = getLinkIconDisplay(link);
                  return (
                    <a 
                      key={i}
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="badge bg-light text-dark text-decoration-none border d-inline-flex align-items-center gap-1"
                    style={{ padding: '0.2rem 0.45rem' }}
                    >
                      <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '1.5em', height: '1.5em' }}>
                        {iconDisplay.iconUrl ? (
                          <img src={iconDisplay.iconUrl} alt="" style={{ width: '1.5em', height: '1.5em', objectFit: 'contain' }} />
                        ) : (
                          <i className={`bi ${iconDisplay.iconClass || 'bi-link-45deg'}`} style={{ fontSize: '1.5em', lineHeight: 1 }}></i>
                        )}
                      </span>
                      <span>{link.name || 'Link'}</span>
                    </a>
                  );
                })}
                {nextStopLink && (
                  <a 
                    href={nextStopLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="badge bg-light text-dark text-decoration-none border d-inline-flex align-items-center gap-1"
                    style={{ padding: '0.2rem 0.45rem' }}
                  >
                    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '1.5em', height: '1.5em' }}>
                      <i className="bi bi-link-45deg" style={{ fontSize: '1.5em', lineHeight: 1 }}></i>
                    </span>
                    <span>{nextStopLink.name}</span>
                  </a>
                )}
              </div>
            )}
            
            {/* Images - moved to the end */}
            {activity.images && activity.images.length > 0 && (
              <div className="mt-2">
                {/* Mobile: Show only 2 images in a row */}
                <div className="d-md-none">
                  <div className="d-flex gap-2">
                    {activity.images.slice(0, 2).map((image, i) => (
                      image.url && (
                        <div 
                          key={i}
                          style={{ 
                            flex: activity.images.length === 1 ? '0 0 auto' : '1 1 calc(50% - 0.25rem)',
                            maxWidth: activity.images.length === 1 ? '120px' : 'none'
                          }}
                        >
                          <img 
                            src={image.url} 
                            alt={image.type || 'Accommodation image'} 
                            className="img-thumbnail"
                            style={{ 
                              height: '120px',
                              width: activity.images.length === 1 ? '120px' : '100%',
                              objectFit: 'contain',
                              backgroundColor: '#f8f9fa'
                            }}
                          />
                        </div>
                      )
                    ))}
                  </div>
                </div>
                {/* Desktop: Inline layout */}
                <div className="d-none d-md-block">
                  {activity.images.map((image, i) => (
                    image.url && (
                      <img 
                        key={i}
                        src={image.url} 
                        alt={image.type || 'Accommodation image'} 
                        className="img-thumbnail me-2"
                        style={{ maxHeight: '150px', maxWidth: '200px' }}
                      />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
} 